import React, {
  useState,
  useMemo,
  useCallback,
  useContext,
  createContext,
  useEffect,
} from 'react';
import {useErrorLogger} from '@shopify/react-bugsnag';
import {usePageContext} from '../contexts/PageContext.client';
import {MonorailEvent, trackEvent} from '../utils/monorail.client';
import {ShoppingBagEvent} from '../utils/shoppingBagAnalytics';

type CartContext = {
  isCartOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
  toggleCart: () => void;
};

export const CartContext = createContext<CartContext | undefined>(undefined);

/**
 * A client component that defines the behavior that occurs when a user is interacting with a cart (for example, opening or closing it)
 */
export default function CartUIProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);

  const pageCtx = usePageContext();
  const logger = useErrorLogger();

  const trackOpenCartEvent = useCallback(() => {
    trackEvent({
      event: MonorailEvent.TrackShoppingBag,
      eventType: ShoppingBagEvent.ClickBagIcon,
      data: {
        pageId: pageCtx.id,
      },
    })?.catch((error) => {
      logger.notify(
        new Error(
          `event tracking error: ${MonorailEvent.TrackShoppingBag} / ${ShoppingBagEvent.ClickBagIcon}: ${error}`,
        ),
      );
    });
  }, [pageCtx]);

  useEffect(() => {
    if (open) {
      trackOpenCartEvent();
    }
  }, [open]);

  const openCart = useCallback(() => {
    setOpen(true);
  }, [setOpen]);

  const closeCart = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const toggleCart = useCallback(() => {
    setOpen(!open);
  }, [setOpen, open]);

  const contextValue = useMemo(() => {
    return {
      isCartOpen: open,
      openCart,
      closeCart,
      toggleCart,
    };
  }, [open, openCart, closeCart, toggleCart]);

  return (
    <CartContext.Provider value={contextValue}>{children}</CartContext.Provider>
  );
}

export function useCartUI() {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCartUI must be used within a CartUIProvider');
  }
  return context;
}
